import type {CmLogger} from "@cm/log"
import type {TimeoutImplementationType} from "@cm/utils"
import type {Observable, Subscription} from "rxjs"
import {firstValueFrom, ReplaySubject, Subject} from "rxjs"

export type TransferListItem = ArrayBuffer
export type MsgEvent = {data: any}

/**
 * Serialized error format used for worker thread communication.
 * Error objects don't survive structured clone across worker boundaries,
 * so we serialize them to this format and reconstruct on the receiving end.
 */
interface SerializedWorkerError {
    __isSerializedError: true
    message: string
    name: string
    stack?: string
}

function isSerializedWorkerError(data: unknown): data is SerializedWorkerError {
    return typeof data === "object" && data !== null && "__isSerializedError" in data && (data as SerializedWorkerError).__isSerializedError === true
}

function deserializeWorkerError(serialized: SerializedWorkerError): Error {
    const error = new Error(serialized.message)
    error.name = serialized.name
    if (serialized.stack) {
        error.stack = serialized.stack
    }
    return error
}

export interface IThreadWorker {
    postMessage(message: any, transfer?: TransferListItem[]): void

    terminate(): void

    observableFromEvent(event: string): Observable<MsgEvent>
}

type WorkerId = number

interface WorkerJob<MessageT, ResponseT> {
    message: MessageT
    transferList?: TransferListItem[]
    response$: Subject<ResponseT>
}

class MonitoredWorker<TimoutIdType> {
    private _idle = true
    private idleTimer: TimoutIdType | null = null
    readonly idle$ = new Subject<void>()
    readonly timeout$ = new Subject<void>()
    private currentJob: WorkerJob<unknown, unknown> | null = null

    constructor(
        readonly id: WorkerId,
        private worker: IThreadWorker,
        readonly name: string,
        private timeoutImplementation: TimeoutImplementationType<TimoutIdType>,
        private logger: CmLogger,
        public maxIdleTime = 5000,
    ) {}

    postJob(job: WorkerJob<unknown, unknown>) {
        let errorSubscription: Subscription | null = null
        const messageSubscription: Subscription = this.worker.observableFromEvent("message").subscribe(
            (event: MsgEvent) => {
                messageSubscription.unsubscribe()
                errorSubscription?.unsubscribe()
                this.currentJob = null
                this.setIdle(true)
                // Check for serialized error format (Error objects don't survive structured clone)
                if (isSerializedWorkerError(event.data)) {
                    const error = deserializeWorkerError(event.data)
                    this.logger.error("Exception in worker:", error.message)
                    job.response$.error(error)
                } else if (event.data instanceof Error) {
                    // Legacy check for direct Error objects (may not work in all cases)
                    job.response$.error(event.data)
                } else {
                    job.response$.next(event.data)
                }
                job.response$.complete()
            },
            (error: any) => {
                messageSubscription.unsubscribe()
                errorSubscription?.unsubscribe()
                this.currentJob = null
                this.setIdle(true)
                this.logger.error("WebWorker error:", error)
                job.response$.error(error)
            },
        )
        errorSubscription = this.worker.observableFromEvent("error").subscribe((event: MsgEvent) => {
            messageSubscription.unsubscribe()
            errorSubscription?.unsubscribe()
            this.currentJob = null
            this.setIdle(true)
            // Worker "error" events are ErrorEvent objects with {message, filename, lineno, error},
            // NOT MessageEvent with {data}. The raw event is wrapped by fromEvent.
            const rawEvent = event as any
            const errorMessage = rawEvent.message ?? rawEvent.error?.message ?? String(rawEvent)
            this.logger.error("Worker error:", errorMessage)
            job.response$.error(new Error(errorMessage))
        })
        this.setIdle(false)
        this.currentJob = job
        this.worker.postMessage(job.message, job.transferList)
    }

    private setIdle(idle: boolean) {
        if (idle && !this._idle) {
            this._idle = idle
            if (this.idleTimer == null) {
                this.idleTimer = this.timeoutImplementation.setTimeout(() => {
                    this.idleTimer = null
                    this.timeout$.next()
                }, this.maxIdleTime)
            }
            this.idle$.next()
        } else if (!idle && this._idle) {
            this._idle = idle
            if (this.idleTimer != null) {
                this.timeoutImplementation.clearTimeout(this.idleTimer)
                this.idleTimer = null
            }
        }
    }

    get idle() {
        return this._idle
    }

    terminate() {
        if (this.idleTimer != null) {
            this.timeoutImplementation.clearTimeout(this.idleTimer)
            this.idleTimer = null
        }
        if (this.currentJob) {
            this.currentJob.response$.error("Worker terminated")
        }
        this.idle$.complete()
        this.timeout$.complete()
        this.worker.terminate()
    }
}

export class WebAssemblyWorkerManager<TimoutIdType> {
    private jobQueue: WorkerJob<any, any>[] = []
    private workers = new Map<WorkerId, MonitoredWorker<TimoutIdType>>()
    private initialWorkers: number
    private maxWorkers: number

    constructor(
        private spawnNewThreadWorker: (name: string) => IThreadWorker,
        private timeoutImplementation: TimeoutImplementationType<TimoutIdType>,
        private logger: CmLogger,
        isMobileDevice: boolean,
        deviceMemory: number | undefined,
    ) {
        const limitWorkers = this.limitWorkers(deviceMemory, isMobileDevice)
        this.initialWorkers = limitWorkers ? 1 : 6
        this.maxWorkers = limitWorkers ? 1 : 6
    }

    private limitWorkers(deviceMemory: number | undefined, isMobileDevice: boolean) {
        if (deviceMemory !== undefined) return deviceMemory < 4
        return isMobileDevice
    }

    private spawnNewWorker(): MonitoredWorker<TimoutIdType> {
        let workerId = 0
        while (this.workers.has(workerId)) ++workerId // find free ID
        const name = `wasm-worker-${workerId}`
        const _worker = this.spawnNewThreadWorker(name)
        const worker = new MonitoredWorker(workerId, _worker, name, this.timeoutImplementation, this.logger)
        this.logger.info(`Starting worker: ${worker.name}`)
        worker.timeout$.subscribe(() => {
            this.terminateWorker(worker)
        })
        worker.idle$.subscribe(() => {
            this.processJobQueue(worker)
        })
        this.workers.set(workerId, worker)
        return worker
    }

    private getIdleWorker(): MonitoredWorker<TimoutIdType> | null {
        for (const worker of this.workers.values()) {
            if (worker.idle) {
                return worker
            }
        }
        return null
    }

    private terminateWorker(worker: MonitoredWorker<TimoutIdType>) {
        // console.log(`Terminating worker: ${worker.name}`);
        this.workers.delete(worker.id)
        worker.terminate()
    }

    private processJobQueue(worker: MonitoredWorker<TimoutIdType> | null = null) {
        const job = this.jobQueue.shift()
        if (!job) return
        // we have jobs left...
        if (!worker) {
            worker = this.getIdleWorker()
        }
        if (!worker && this.workers.size < this.maxWorkers) {
            worker = this.spawnNewWorker()
        }
        if (worker) {
            worker.postJob(job)
        } else {
            // all workers are occupied, put the job back and try again later
            this.jobQueue.unshift(job)
        }
    }

    enqueueJob(job: WorkerJob<any, any>): void {
        this.jobQueue.push(job)
        this.processJobQueue()
    }

    invokeFunction<ResponseT>(functionName: string, args: any[], transfers?: TransferListItem[]): Observable<ResponseT> {
        const response$: ReplaySubject<ResponseT> = new ReplaySubject<ResponseT>(1)
        this.enqueueJob({
            message: {functionName, args},
            transferList: transfers,
            response$,
        })
        return response$
    }

    startInitialWorkers(): void {
        while (this.workers.size < this.initialWorkers) {
            const worker = this.spawnNewWorker()
            this.processJobQueue(worker)
        }
    }

    terminateJobsAndWorkers(): void {
        for (const workerId of [...this.workers.keys()]) {
            const worker = this.workers.get(workerId)
            if (worker) this.terminateWorker(worker)
        }
        const tmpJobs = this.jobQueue
        this.jobQueue = []
        for (const job of tmpJobs) {
            job.response$.error("Cancelled")
        }
    }

    get activeWorkers() {
        let count = 0
        for (const worker of this.workers.values()) {
            if (!worker.idle) {
                ++count
            }
        }
        return count
    }

    invokeFunctionAsPromise<ResponseT>(functionName: string, args: unknown[], transfers?: TransferListItem[]): Promise<ResponseT> {
        const response$: ReplaySubject<ResponseT> = new ReplaySubject<ResponseT>(1)
        this.enqueueJob({
            message: {functionName, args},
            transferList: transfers,
            response$,
        })
        return firstValueFrom(response$)
    }
}
